import React, { useEffect, useState } from "react";
import { closeModal, dialogConfirm } from "$stores/modal";
import DateInput from "$components/form-inputs/date-picker";

const EditActionItemModal = ({
    item,
    collaborators,
    onSave,
    onCancelItem,
    isOpen,
}) => {
    const [text, setText] = useState(item.commitmentText);
    const [dueDate, setDueDate] = useState(item.dueDate ? new Date(item.dueDate) : null);
    const [assigneeId, setAssigneeId] = useState(item.assignedToUserId || "");
    const [saving, setSaving] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState(null);

    const hadDate = !!item.dueDate;
    const noCollaborators = collaborators.length === 0;

    // Reset state when modal opens for a (possibly different) item.
    // Dep on item.id ensures re-init when opened for a new item without unmounting.
    useEffect(() => {
        if (isOpen) {
            setText(item.commitmentText);
            setDueDate(item.dueDate ? new Date(item.dueDate) : null);
            setAssigneeId(item.assignedToUserId || "");
            setError(null);
            setSaving(false);
            setCancelling(false);
        }
    }, [isOpen, item.id]);

    const handleSave = async () => {
        if (!text.trim()) return;
        setSaving(true);
        setError(null);
        try {
            await onSave({
                commitmentText: text.trim(),
                dueDate: dueDate ? dueDate.toISOString() : null,
                clearDueDate: hadDate && !dueDate,
                assignedToUserId: assigneeId || null,
                clearAssignedUser: !assigneeId,
            });
            closeModal();
        } catch {
            setError("Failed to save. Please try again.");
            setSaving(false);
        }
    };

    const handleCancelItem = async () => {
        const confirmed = await dialogConfirm({
            data: { message: "Are you sure you want to cancel this action item? This cannot be undone." },
        });
        if (!confirmed) return;
        setCancelling(true);
        try {
            await onCancelItem();
            closeModal();
        } catch {
            setError("Failed to cancel item. Please try again.");
            setCancelling(false);
        }
    };

    return (
        <div className="wizard action-item-modal">
            <h3>Edit Action Item</h3>

            <div className="form-field -full">
                <label>What needs to be done?</label>
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoFocus
                />
            </div>

            <DateInput
                id="edit-dueDate"
                label="Due date (optional)"
                defaultValue={dueDate}
                updateOnChange={(id, date) => setDueDate(date)}
            />

            <div className="form-field -full">
                <label>Assign to (optional)</label>
                <select
                    value={assigneeId}
                    onChange={(e) => setAssigneeId(e.target.value)}
                    disabled={noCollaborators}
                >
                    <option value="">
                        {noCollaborators ? "No collaborators yet" : "— Unassigned —"}
                    </option>
                    {collaborators.map((c) => (
                        <option key={c.userId} value={c.userId}>
                            {c.userName}
                        </option>
                    ))}
                </select>
            </div>

            {error && <p className="txt -error">{error}</p>}

            <div className="finalize">
                <div className="button-group -right">
                    <button
                        className="button -error -small"
                        onClick={handleCancelItem}
                        disabled={saving || cancelling}
                    >
                        {cancelling ? "Cancelling…" : "Cancel Action Item"}
                    </button>
                    <button
                        className="button -text -small"
                        onClick={closeModal}
                        disabled={saving || cancelling}
                    >
                        Close
                    </button>
                    <button
                        className="button -primary -small"
                        onClick={handleSave}
                        disabled={!text.trim() || saving || cancelling}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditActionItemModal;
